// Procedimientos de SST para trabajos especiales (Anexo 1 GT 37): uno por cada tipo de permiso de trabajo, generado con
// lo que ya define el tipo (preguntas del permiso y peligros de la Matriz de Riesgos), editable, aprobado con firma (o
// con el formato firmado a mano y su escaneo) y socializado: los trabajadores firman que lo recibieron (registro
// firmado, como la asistencia a capacitaciones). Se abre desde Permisos de Trabajo (módulo oculto para clientes).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import SignaturePad, { type SignaturePadHandle } from '../permits/SignaturePad';
import RegistroFirmasModal from '../firmas/RegistroFirmasModal';
import { cargarFirmas, type DatosFirmas } from '../../lib/firmas/firmas-datos';
import { estadoVisible, faltantesContenido } from '../../lib/firmas/firmas-logica';
import {
  ACEPTA_ARCHIVO, aprobacionParaPdf, aprobarVersion, cargarProcedimientos, contenidoSocializacion, guardarProcedimiento, urlArchivo, type DatosProcedimientos,
} from '../../lib/informes-puesto/ip-datos';
import { isoDia, ultimaVersion } from '../../lib/informes-puesto/ip-logica';
import { resultadoProcedimientos } from '../../lib/informes-puesto/ip-checklist';
import {
  borradorProcedimiento, contenidoProcedimiento, ETIQUETA_TIPO_PERMISO, faltantesProcedimiento, hashProcedimiento, TIPOS_PERMISO,
  type ContenidoProcedimiento, type EditableProcedimiento, type FilaProcedimiento, type TipoPermiso,
} from '../../lib/informes-puesto/ip-procedimientos';
import { TEXTO_FIRMA_APROBACION, type FilaVersion } from '../../lib/informes-puesto/ip-tipos';
import { abrirConUrl, boton, chip, fechaEc, msg, S } from './estilos';

// El PDF (jsPDF) se carga al usarlo.
const pdfs = () => import('../../lib/informes-puesto/ip-pdf');

export interface ProcedimientosPanelProps { companyId: string; empresa: string; usuarioId: string | null; puedeEditar: boolean; esAdmin: boolean }

interface Trabajador { id: string; full_name: string; cedula: string | null; position_id: string | null; status: string | null }
const CAMPOS: [keyof EditableProcedimiento, string][] = [['objective', 'Objetivo'], ['scope', 'Alcance'], ['responsibles', 'Responsables'],
  ['activities', 'Actividades'], ['risks', 'Riesgos'], ['controls', 'Controles'], ['ppe', 'Equipo de protección personal']];

export default function ProcedimientosPanel(p: ProcedimientosPanelProps) {
  const [datos, setDatos] = useState<DatosProcedimientos | null>(null);
  const [firmas, setFirmas] = useState<DatosFirmas>({ registros: [], firmas: [], itemsEpp: [] });
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [tipo, setTipo] = useState<TipoPermiso | null>(null);
  const [form, setForm] = useState<EditableProcedimiento | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [metodo, setMetodo] = useState<'pantalla' | 'papel'>('pantalla');
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [conTrazo, setConTrazo] = useState(false);
  const [escaneo, setEscaneo] = useState<File | null>(null);
  const [soc, setSoc] = useState<{ fecha: string; quien: string; material: string; ids: string[] } | null>(null);
  const [registroAbierto, setRegistroAbierto] = useState<{ registroId: string | null; version: FilaVersion; contenidoSoc: ReturnType<typeof contenidoSocializacion> | null } | null>(null);
  const pad = useRef<SignaturePadHandle>(null);

  const cargar = useCallback(async () => {
    setError('');
    try {
      const [d, f, { data: w }] = await Promise.all([cargarProcedimientos(p.companyId), cargarFirmas(p.companyId, 'socializacion').catch(() => ({ registros: [], firmas: [], itemsEpp: [] })),
        supabase.from('workers').select('id, full_name, cedula, position_id, status').eq('company_id', p.companyId).order('full_name')]);
      setDatos(d); setFirmas(f); setTrabajadores(((w ?? []) as Trabajador[]).filter(x => (x.status ?? 'activo') === 'activo'));
    } catch (e) { setError(msg(e)); }
  }, [p.companyId]);
  useEffect(() => { cargar(); }, [cargar]);

  const proc = (t: TipoPermiso) => datos?.procedimientos.find(x => x.permit_type === t) ?? null;
  const versionesDe = (pr: FilaProcedimiento | null) => (pr && datos ? datos.versiones.filter(v => v.procedure_id === pr.id).sort((a, b) => b.version - a.version) : []);
  const res = datos ? resultadoProcedimientos(datos.permisos, datos.procedimientos, datos.versiones,
    firmas.registros.map(r => ({ procedure_version_id: r.procedure_version_id ?? null, status: r.status })), new Date()) : null;

  const abrir = (t: TipoPermiso) => {
    const pr = proc(t);
    setTipo(t); setError(''); setOk(''); setSoc(null);
    setForm(pr ? { objective: pr.objective, scope: pr.scope, responsibles: pr.responsibles, position_ids: pr.position_ids, exposed_count: pr.exposed_count,
      exposed_notes: pr.exposed_notes, activities: pr.activities, risks: pr.risks, controls: pr.controls, ppe: pr.ppe }
      : borradorProcedimiento(t, datos?.preguntas[t] ?? [], datos?.peligros ?? []));
  };
  const ejecutar = async (fn: () => Promise<void>, exito?: string) => {
    setOcupado(true); setError(''); setOk('');
    try { await fn(); await cargar(); if (exito) setOk(exito); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  };
  const contenidoActual = (pr: FilaProcedimiento): ContenidoProcedimiento =>
    contenidoProcedimiento({ ...pr, ...form! }, p.empresa, datos?.puestos ?? [], datos?.preguntas[pr.permit_type] ?? [], isoDia(new Date()));
  const guardar = () => ejecutar(async () => { await guardarProcedimiento(p.companyId, proc(tipo!), tipo!, form!); }, 'Procedimiento guardado.');
  const aprobar = () => ejecutar(async () => {
    const pr = await guardarProcedimiento(p.companyId, proc(tipo!), tipo!, form!);
    const c = contenidoActual(pr);
    const f = faltantesProcedimiento(c); if (f.length) throw new Error(f.join(' '));
    const hash = await hashProcedimiento(c);
    const doc = { tipo: 'procedimiento' as const, procedimientoId: pr.id, permitType: pr.permit_type };
    if (metodo === 'pantalla') {
      if (!conTrazo || pad.current?.isEmpty()) throw new Error('Firma en el recuadro.');
      const trazo = await pad.current!.toBlob(); if (!trazo) throw new Error('No se pudo leer la firma.');
      await aprobarVersion(p.companyId, doc, c, hash, { metodo: 'pantalla', nombre, cedula, trazo });
    } else {
      if (!escaneo) throw new Error('Sube el escaneo del formato firmado.');
      await aprobarVersion(p.companyId, doc, c, hash, { metodo: 'papel', escaneo });
    }
    pad.current?.clear(); setConTrazo(false); setEscaneo(null);
  }, 'Versión aprobada. Para cambiar algo, aprueba una versión nueva y vuelve a socializarla.');
  const pdf = async (pr: FilaProcedimiento | null, v: FilaVersion | null, blanco: boolean) => {
    try {
      const { generarProcedimientoPDF, nombreArchivo } = await pdfs();
      if (v) { generarProcedimientoPDF({ contenido: v.snapshot as unknown as ContenidoProcedimiento, hash: v.content_hash, aprobacion: await aprobacionParaPdf(v), blanco: false, fecha: new Date() })
        .save(nombreArchivo(`Procedimiento_${ETIQUETA_TIPO_PERMISO[tipo!]}`, `_v${v.version}`)); return; }
      const base: FilaProcedimiento = pr ?? { id: 'borrador', company_id: p.companyId, permit_type: tipo!, created_at: '', updated_at: '', ...form! } as FilaProcedimiento;
      const c = contenidoActual(base);
      generarProcedimientoPDF({ contenido: c, hash: blanco ? await hashProcedimiento(c) : null, aprobacion: null, blanco, fecha: new Date() })
        .save(nombreArchivo(`Procedimiento_${ETIQUETA_TIPO_PERMISO[tipo!]}`, blanco ? '_para_firmar' : '_borrador'));
    } catch (e) { setError(msg(e)); }
  };

  const pr = tipo ? proc(tipo) : null;
  const versiones = versionesDe(pr);
  const vigente = ultimaVersion(versiones);
  const socDe = (v: FilaVersion) => firmas.registros.filter(r => r.procedure_version_id === v.id);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-label="Procedimientos de trabajos especiales">
      <div style={S.bloque}>
        <h3 style={S.h3}>Procedimientos de trabajos especiales</h3>
        <p style={S.nota}>Un procedimiento por tipo de permiso: objetivo, responsables, puestos, expuestos, actividades, riesgos, controles, EPP y el formato del permiso. Se aprueba con firma y se socializa con la firma de cada trabajador.</p>
        {res && <p style={S.texto}>{res.resumen}</p>}
      </div>
      {error && <p style={S.error} role="alert">{error}</p>}
      {ok && <p style={S.ok} role="status">{ok}</p>}
      <div style={S.rejilla}>
        {TIPOS_PERMISO.map(t => {
          const x = proc(t); const v = ultimaVersion(versionesDe(x));
          const socializado = !!v && firmas.registros.some(r => r.procedure_version_id === v.id && r.status === 'firmado');
          const usado = res?.usados.includes(t);
          const [texto, color] = !x ? ['Sin procedimiento', '#94a3b8'] : !v ? ['En borrador', '#f59e0b'] : socializado ? [`Versión ${v.version} socializada`, '#2FBF71'] : [`Versión ${v.version} sin socializar`, '#f59e0b'];
          return (
            <button key={t} style={{ ...boton(tipo === t ? 'primario' : 'secundario'), flex: '1 1 150px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, textAlign: 'left' }} onClick={() => abrir(t)}>
              <span>{ETIQUETA_TIPO_PERMISO[t]}</span>
              <span style={chip(tipo === t ? '#0f172a' : color)}>{texto}</span>
              {usado && <span style={{ fontSize: '0.7rem' }}>Usado en los últimos 12 meses</span>}
            </button>
          );
        })}
      </div>

      {tipo && form && (
        <div style={S.bloque}>
          <h3 style={S.h3}>{ETIQUETA_TIPO_PERMISO[tipo]}</h3>
          {!pr && <p style={S.nota}>Borrador generado con las preguntas del permiso de este tipo y los peligros de la Matriz de Riesgos: revísalo y guárdalo.</p>}
          {CAMPOS.map(([k, etiqueta]) => (
            <label key={k} style={S.etiqueta}>{etiqueta}
              <textarea style={{ ...S.input, minHeight: k === 'activities' || k === 'controls' ? 120 : 70 }} value={(form[k] as string | null) ?? ''} disabled={!p.puedeEditar}
                onChange={e => setForm({ ...form, [k]: e.target.value })} />
            </label>
          ))}
          <fieldset style={{ border: '1px solid #334155', borderRadius: 8, padding: 8, minWidth: 0 }}>
            <legend style={S.nota}>Puestos de trabajo</legend>
            <div style={S.rejilla}>
              {(datos?.puestos ?? []).map(x => (
                <label key={x.id} style={{ ...S.nota, display: 'flex', gap: 6, alignItems: 'center', flex: '1 1 160px' }}>
                  <input type="checkbox" disabled={!p.puedeEditar} checked={form.position_ids.includes(x.id)}
                    onChange={e => setForm({ ...form, position_ids: e.target.checked ? [...form.position_ids, x.id] : form.position_ids.filter(i => i !== x.id) })} />
                  {x.name}
                </label>
              ))}
            </div>
          </fieldset>
          <div style={S.rejilla}>
            <label style={S.etiqueta}>Trabajadores expuestos
              <input inputMode="numeric" style={S.input} disabled={!p.puedeEditar} value={form.exposed_count ?? ''}
                onChange={e => setForm({ ...form, exposed_count: e.target.value === '' ? null : Math.max(0, Number(e.target.value.replace(/\D/g, ''))) })} />
            </label>
            <label style={{ ...S.etiqueta, flex: '2 1 220px' }}>Detalle de expuestos
              <input style={S.input} disabled={!p.puedeEditar} value={form.exposed_notes ?? ''} onChange={e => setForm({ ...form, exposed_notes: e.target.value })} />
            </label>
          </div>
          <div style={S.botones}>
            {p.puedeEditar && <button style={boton('secundario', ocupado)} disabled={ocupado} onClick={guardar}>Guardar borrador</button>}
            <button style={boton()} onClick={() => pdf(pr, null, false)}>PDF del borrador</button>
          </div>

          {p.puedeEditar && (
            <div style={{ ...S.bloque, background: '#111827' }}>
              <h3 style={S.h3}>Aprobar la versión {(vigente?.version ?? 0) + 1}</h3>
              <div style={S.pestanas}>
                <button style={boton(metodo === 'pantalla' ? 'primario' : 'secundario')} onClick={() => setMetodo('pantalla')}>Firmar en pantalla</button>
                <button style={boton(metodo === 'papel' ? 'primario' : 'secundario')} onClick={() => setMetodo('papel')}>Firma manual</button>
              </div>
              {metodo === 'pantalla' ? (<>
                <div style={S.rejilla}>
                  <label style={S.etiqueta}>Nombre del responsable de SSO<input style={S.input} value={nombre} onChange={e => setNombre(e.target.value)} /></label>
                  <label style={S.etiqueta}>Cédula<input inputMode="numeric" maxLength={10} style={S.input} value={cedula} onChange={e => setCedula(e.target.value.replace(/\D/g, ''))} /></label>
                </div>
                {/* El pad trae su propio botón "Borrar firma" (avisa por onChange): no se repite aquí. */}
                <SignaturePad ref={pad} onChange={v => setConTrazo(!v)} />
                <div style={S.botones}>
                  <button style={boton('primario', ocupado)} disabled={ocupado} onClick={aprobar}>Aprobar y firmar</button>
                </div>
              </>) : (<>
                <div style={S.botones}><button style={boton()} onClick={() => pdf(pr, null, true)}>Imprimir para firma manual</button></div>
                <label style={S.etiqueta}>Escaneo firmado (PDF, PNG o JPEG)
                  <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setEscaneo(e.target.files?.[0] ?? null)} style={{ color: '#cbd5e1', maxWidth: '100%' }} />
                </label>
                <button style={boton('primario', ocupado)} disabled={ocupado} onClick={aprobar}>Aprobar con el escaneo</button>
              </>)}
              <p style={S.nota}>{TEXTO_FIRMA_APROBACION}</p>
            </div>
          )}

          <h3 style={S.h3}>Versiones aprobadas y socialización</h3>
          {!versiones.length && <p style={S.nota}>Todavía no hay versiones aprobadas.</p>}
          {versiones.map(v => (
            <div key={v.id} style={{ ...S.fila, flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                  <div style={{ ...S.texto, fontWeight: 600 }}>Versión {v.version}{v.id === vigente?.id ? ' (la vigente)' : ''}</div>
                  <div style={S.nota}>{new Date(v.approved_at).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' })} · {v.sign_method === 'pantalla' ? `${v.signer_name} (C.I. ${v.signer_cedula})` : 'formato firmado a mano'}</div>
                </div>
                <div style={S.botones}>
                  <button style={boton()} onClick={() => pdf(pr, v, false)}>PDF</button>
                  {v.scan_path && <button style={boton()} onClick={() => abrirConUrl(urlArchivo(v.scan_path!))}>Escaneo</button>}
                </div>
              </div>
              {socDe(v).map(r => (
                <div key={r.id} style={S.fila}>
                  <span style={S.texto}>Socialización del {fechaEc(r.record_date)} · {estadoVisible(r, firmas.firmas.filter(f => f.record_id === r.id)).texto}</span>
                  <button style={boton()} onClick={() => setRegistroAbierto({ registroId: r.id, version: v, contenidoSoc: null })}>Abrir</button>
                </div>
              ))}
              {v.id === vigente?.id && p.puedeEditar && !soc && (
                <div style={S.botones}><button style={boton('primario')} onClick={() => setSoc({ fecha: isoDia(new Date()), quien: '', material: `Procedimiento ${ETIQUETA_TIPO_PERMISO[tipo]} (versión ${v.version})`,
                  ids: trabajadores.filter(w => w.position_id && (v.snapshot as { position_ids?: string[] }).position_ids?.includes(w.position_id)).map(w => w.id) })}>Nueva socialización</button></div>
              )}
              {v.id === vigente?.id && soc && (
                <div style={{ ...S.bloque, background: '#111827' }}>
                  <div style={S.rejilla}>
                    <label style={S.etiqueta}>Fecha<input type="date" style={S.input} value={soc.fecha} onChange={e => setSoc({ ...soc, fecha: e.target.value })} /></label>
                    <label style={S.etiqueta}>Socializado por<input style={S.input} value={soc.quien} onChange={e => setSoc({ ...soc, quien: e.target.value })} /></label>
                    <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Material<input style={S.input} value={soc.material} onChange={e => setSoc({ ...soc, material: e.target.value })} /></label>
                  </div>
                  <fieldset style={{ border: '1px solid #334155', borderRadius: 8, padding: 8, minWidth: 0 }}>
                    <legend style={S.nota}>Trabajadores que lo reciben ({soc.ids.length})</legend>
                    <div style={S.rejilla}>
                      {trabajadores.map(w => (
                        <label key={w.id} style={{ ...S.nota, display: 'flex', gap: 6, alignItems: 'center', flex: '1 1 180px' }}>
                          <input type="checkbox" checked={soc.ids.includes(w.id)} onChange={e => setSoc({ ...soc, ids: e.target.checked ? [...soc.ids, w.id] : soc.ids.filter(i => i !== w.id) })} />
                          {w.full_name}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div style={S.botones}>
                    <button style={boton('primario')} onClick={() => setRegistroAbierto({ registroId: null, version: v,
                      contenidoSoc: contenidoSocializacion(v, ETIQUETA_TIPO_PERMISO[tipo], p.empresa, soc.fecha, soc.quien, soc.material, trabajadores.filter(w => soc.ids.includes(w.id))) })}>
                      Recoger las firmas
                    </button>
                    <button style={boton()} onClick={() => setSoc(null)}>Cancelar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {registroAbierto && (() => {
        const reg = registroAbierto.registroId ? firmas.registros.find(r => r.id === registroAbierto.registroId) ?? null : null;
        const c = registroAbierto.contenidoSoc;
        return (
          <RegistroFirmasModal
            titulo={`Socialización · ${tipo ? ETIQUETA_TIPO_PERMISO[tipo] : ''} (versión ${registroAbierto.version.version})`}
            companyId={p.companyId} usuarioId={p.usuarioId} registro={reg} firmas={reg ? firmas.firmas.filter(f => f.record_id === reg.id) : []}
            origen={{ procedure_version_id: registroAbierto.version.id }}
            armarContenido={async () => {
              const contenido = c ?? (reg?.snapshot as ReturnType<typeof contenidoSocializacion>);
              return { contenido, faltantes: faltantesContenido(contenido) };
            }}
            puedeEditar={p.puedeEditar} esAdmin={p.esAdmin}
            onCambio={() => { setSoc(null); cargar(); }} onCerrar={() => setRegistroAbierto(null)}
          />
        );
      })()}
    </section>
  );
}
